import { useParams, useNavigate } from 'react-router-dom'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import useQueryGetTournamentById from '@/modules/tournament/api/hooks/useQueryGetTournamentById'
import TournamentForm from '@/modules/tournament/components/TournamentForm'
import { Card, CardContent } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { ArrowLeft } from 'lucide-react'

function EditTournamentPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { data: tournament, isLoading } = useQueryGetTournamentById(id || '')

	if (isLoading) {
		return (
			<PageContainer>
				<Card>
					<CardContent className="p-6">
						<div className="flex h-32 items-center justify-center">
							<div className="text-muted-foreground">กำลังโหลด...</div>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		)
	}

	if (!tournament) {
		return (
			<PageContainer>
				<Card>
					<CardContent className="p-6">
						<div className="flex h-32 items-center justify-center">
							<div className="text-destructive">ไม่พบทัวร์นาเมนต์</div>
						</div>
					</CardContent>
				</Card>
			</PageContainer>
		)
	}

	const initialData = {
		name: tournament.name,
		description: tournament.description || undefined,
		bannerImage: tournament.bannerImage || undefined,
		startDate: new Date(tournament.startDate),
		endDate: new Date(tournament.endDate),
		minTurnover: tournament.minTurnover
			? Number(tournament.minTurnover)
			: undefined,
		minDeposit: tournament.minDeposit
			? Number(tournament.minDeposit)
			: undefined,
		minBetAmount: Number(tournament.minBetAmount),
		maxTransactionDay: tournament.maxTransactionDay,
		leaderboardRefresh: tournament.leaderboardRefresh,
		pointSystemConfig: tournament.pointSystemConfig as
			| { streakBonuses: Record<string, number> }
			| undefined,
		allowedGames: tournament.allowedGames,
		allowedProviders: tournament.allowedProviders,
		isActive: tournament.isActive,
		prizes: tournament.prizes.map((prize) => ({
			rank: prize.rank,
			prizeAmount: Number(prize.prizeAmount),
		})),
	}

	return (
		<PageContainer>
			<div className="space-y-6">
				<div className="flex items-start gap-4">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => navigate('/operator/tournaments')}
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div className="flex-1">
						<h1 className="text-2xl font-bold">แก้ไขทัวร์นาเมนต์</h1>
						<p className="text-sm text-muted-foreground">{tournament.name}</p>
					</div>
				</div>

				<TournamentForm
					mode="edit"
					tournamentId={id}
					initialData={initialData}
					onSuccess={() => navigate('/operator/tournaments')}
				/>
			</div>
		</PageContainer>
	)
}

export default withUserPermission(EditTournamentPage, {
	permissionType: PermissionType.Write,
	permissionKey: [PermissionKeys.MANAGE_GAME],
})
